import * as React from 'react';
import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { withIcon } from '../../../core/components/Icon.tsx';
import { faEllipsisV } from '@fortawesome/pro-regular-svg-icons';

import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';

import { DELETE_SHAREPOINT_INTEGRATION, GET_SHAREPOINT_INTEGRATIONS } from '../../queries.ts';
import { useToaster } from '../../../core/components/Toast.tsx';

const MoreIcon = withIcon(faEllipsisV);

interface SharePointRowProps {
    integration: any;
    refetch: () => Promise<any>;
}

const SharePointRow = ({ integration, refetch }: SharePointRowProps) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();
    const [isDisconnectModalVisible, setDisconnectModalVisible] = useState(false);

    const [deleteIntegration] = useMutation(DELETE_SHAREPOINT_INTEGRATION, {
        refetchQueries: [GET_SHAREPOINT_INTEGRATIONS],
    });

    const handleDisconnect = async () => {
        try {
            await deleteIntegration({ variables: { integrationId: integration.id } });
            await refetch();
            publishToast({ description: t('sharepoint.disconnectSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('sharepoint.disconnectError'), status: 'error' });
        } finally {
            setDisconnectModalVisible(false);
        }
    };

    return (
        <>
            <Table.Row>
                <Table.Cell>{integration.name || '-'}</Table.Cell>
                <Table.Cell>
                    {integration.enabled ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-surface-success-weak text-content-success-strong border border-line-success-strong">
                            {t('sharepoint.status.connected')}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-surface-warning-weak text-content-warning-strong border border-line-warning-strong">
                            {t('sharepoint.status.disabled')}
                        </span>
                    )}
                </Table.Cell>
                <Table.Cell>{new Date(integration.createdAt).toLocaleString()}</Table.Cell>
                <Table.Cell sticky="right">
                    <DropdownMenu>
                        <DropdownMenu.Trigger asChild>
                            <RowControlIconButton>
                                <MoreIcon />
                            </RowControlIconButton>
                        </DropdownMenu.Trigger>

                        <DropdownMenu.Content align="end">
                            <DropdownMenu.Item danger onClick={() => setDisconnectModalVisible(true)}>
                                {t('sharepoint.moreMenu.disconnect')}
                            </DropdownMenu.Item>
                        </DropdownMenu.Content>
                    </DropdownMenu>
                </Table.Cell>
            </Table.Row>

            {isDisconnectModalVisible && (
                <ConfirmModal
                    onConfirm={handleDisconnect}
                    onCancel={() => setDisconnectModalVisible(false)}
                    title={t('sharepoint.disconnectModal.title')}
                    labelConfirm={t('sharepoint.disconnectModal.disconnectButton')}
                    danger
                >
                    {t('sharepoint.disconnectModal.message', { name: integration.name })}
                </ConfirmModal>
            )}
        </>
    );
};

export default SharePointRow;
